window.theme = window.theme || {}

theme.SourcepointCMP = class SourcepointCMP {
  constructor() {
    this.shopifyCustomerPrivacy = null
    // `cmpType` is set by `sourcepointConsentReady.message_type`
    // ('gdpr' → 'tcf', 'usnat' → 'gpp') and NOT by which CMP stub happens
    // to be on the page. Sourcepoint publishes both `window.__tcfapi` and
    // `window.__gpp` for every visitor; using stub presence to pick the
    // type was the source of US visitors being locked into the TCF path.
    this.cmpType = null
    this.isInitialized = false
    this.consentReady = false
    this.isSyncingConsent = false

    // IAB TCF v2.2 purpose → Shopify Customer Privacy category mapping.
    // Purposes (abridged):
    //   1 Store/access info on device           6 Personalized content
    //   2 Basic ads                             7 Measure ad performance
    //   3 Personalized ads profile              8 Measure content performance
    //   4 Personalized ads                      9 Market research
    //   5 Personalized content profile         10 Develop/improve products
    // Purposes 1 and 2 are covered by legitimate interest / strictly-necessary
    // so we intentionally don't gate Shopify categories on them.
    this.tcfPurposeMapping = {
      marketing: [3, 4],
      analytics: [7, 8, 9, 10],
      preferences: [5, 6]
    }

    this.blockDataCollection()
    this.setupEventListeners()
  }

  setupEventListeners() {
    document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => {
      if (this.isDebugMode()) console.log('[SP] Shopify Customer Privacy API loaded')
      this.shopifyCustomerPrivacy = window.Shopify.customerPrivacy
      this.initializeIntegration()
    })

    document.addEventListener('sourcepointCMPReady', () => {
      if (this.isDebugMode()) console.log('[SP] Sourcepoint CMP ready')
      this.initializeIntegration()
    })

    document.addEventListener('sourcepointConsentReady', (event) => {
      if (this.isDebugMode()) console.log('[SP] Sourcepoint consent ready:', event.detail)

      const { message_type, info } = event.detail || {}

      // Sourcepoint fires `sourcepointConsentReady` for EVERY configured
      // scenario it evaluated, not just the one that applies to this
      // visitor. For a US visitor we receive both `usnat` (applies:true)
      // and `gdpr` (applies:false). We must only honour the scenario
      // Sourcepoint says actually applies, otherwise `cmpType` gets
      // overwritten by the last-fired event and later TCF/GPP listener
      // callbacks can downgrade consent against the wrong mapping.
      if (!info || info.applies !== true) {
        if (this.isDebugMode()) console.log('[SP] Ignoring non-applicable scenario:', message_type)
        return
      }

      // Wire the Privacy Manager link only for the applicable scenario.
      // Doing this before the `applies` guard would briefly attach a
      // GDPR-PM modal handler for a US visitor (or vice versa) before
      // the next event corrects it, leaving the wrong modal wired if
      // the second event never fires.
      this.configurePrivacyManagerLink(message_type, info)

      // `message_type` is the authoritative scenario selector. This MUST
      // be set before `syncConsentToShopify()` so the correct mapping
      // path (TCF for GDPR, GPP/USNAT for US) is taken.
      if (message_type === 'gdpr') {
        this.cmpType = 'tcf'
      } else if (message_type === 'usnat') {
        this.cmpType = 'gpp'
      } else {
        if (this.isDebugMode()) console.warn('[SP] Unrecognized consent message_type:', message_type)
        return
      }

      // `consentReady` means "an applicable scenario has resolved and
      // routing is locked to its mapping." Set it only after we've
      // committed to a `cmpType`. Setting it earlier (e.g. on the first
      // non-applicable event for a US visitor) would briefly open the
      // `allowDataCollection()` gate without a corresponding sync, which
      // is exactly the class of bug the Option A refactor was meant to
      // close.
      this.consentReady = true

      this.syncConsentToShopify()
    })

    // Kept for debug observability only. Consent itself is synced through
    // the `sourcepointConsentReady` → `syncConsentToShopify()` path, which
    // is the single source of truth regardless of which button the user
    // pressed (Accept All, Reject All, Save & Exit, etc.).
    document.addEventListener('sourcepointMessageChoiceSelect', (event) => {
      if (this.isDebugMode()) console.log('[SP] Sourcepoint message choice select:', event.detail)
    })

    // We intentionally do not listen for Shopify's `visitorConsentCollected`
    // event: Sourcepoint is the source of truth for consent, and re-syncing
    // from Shopify → Sourcepoint would create circular updates.

    // Race-condition safety: Shopify's Customer Privacy API dispatches
    // `shopifyCustomerPrivacyApiLoaded` inside the loadFeatures callback,
    // which commonly resolves BEFORE DOMContentLoaded fires (i.e. before
    // this class is instantiated). When that happens the event fires into
    // the void and `this.shopifyCustomerPrivacy` never gets set, so
    // `initializeIntegration()` silently bails on every subsequent event
    // and no consent is ever synced to Shopify. Same pattern can apply to
    // `sourcepointCMPReady`. Fall back to reading the globals directly.
    if (!this.shopifyCustomerPrivacy && window.Shopify && window.Shopify.customerPrivacy) {
      this.shopifyCustomerPrivacy = window.Shopify.customerPrivacy
      if (this.isDebugMode()) console.log('[SP] Shopify Customer Privacy API already loaded, picked up directly')
    }
    this.initializeIntegration()
  }

  isDebugMode() {
    return window.sourcepointConfig && window.sourcepointConfig.debug
  }

  initializeIntegration() {
    if (this.isInitialized) return

    try {
      if (!this.shopifyCustomerPrivacy) {
        if (this.isDebugMode()) console.log('[SP] Shopify Customer Privacy API not ready yet')
        return
      }

      if (this.bindCMP()) {
        this.completeInitialization()
      } else {
        if (this.isDebugMode()) console.warn('[SP] Sourcepoint CMP not available yet, will retry when loaded')
        this.waitForSourcepointCMP()
      }
    } catch (error) {
      console.error('[SP] Failed to initialize Sourcepoint CMP integration:', error)
    }
  }

  bindCMP() {
    // Presence check only. Sourcepoint publishes both `__tcfapi` and
    // `__gpp` stubs on every page, so the presence of a stub is not a
    // signal of which scenario applies to this visitor. The actual CMP
    // type is set later by `sourcepointConsentReady.message_type`.
    return !!(window.__tcfapi || window.__gpp)
  }

  completeInitialization() {
    this.isInitialized = true
    if (this.isDebugMode()) console.log('[SP] Sourcepoint CMP integration initialized successfully')

    // We wire TCF/GPP listeners now but intentionally do NOT kick off a
    // blind initial sync. `cmpType` is still null at this point, and
    // either CMP would give us the wrong picture (TCF reports
    // `gdprApplies: false` for US visitors, GPP reports USNAT state even
    // when GDPR is the applicable scenario). The first correct sync is
    // driven by `sourcepointConsentReady`, whose `message_type` tells us
    // which scenario actually applies.
    this.setupSourcepointEventListeners()

    // After listeners are wired, probe the CMP surfaces directly in case
    // Sourcepoint already finished evaluating scenarios before this class
    // was instantiated (warm cache, deferred theme JS, etc.). Custom
    // events don't replay, so without this probe the integration would
    // silently sit idle until the next user action.
    this.attemptEagerConsentRoute()
  }

  attemptEagerConsentRoute() {
    // Mirrors what the `sourcepointConsentReady` listener would do for
    // an applicable scenario, by querying the CMP APIs directly. Each
    // probe fast-aborts if the listener has already set `cmpType`, so
    // the legitimate-event path always wins the race when both arrive.

    // USNAT probe — Sourcepoint's `_sp_.usnat` namespace exposes the
    // current consent payload independently of __gpp event timing, with
    // an authoritative `applies` boolean.
    const usnat = window._sp_ && window._sp_.usnat
    if (usnat && typeof usnat.getUserConsents === 'function') {
      try {
        usnat.getUserConsents((consents, success) => {
          if (!success || !consents || consents.applies !== true) return
          if (this.cmpType) return
          if (this.isDebugMode()) console.log('[SP] Eagerly routed to USNAT (already-resolved scenario)')
          this.cmpType = 'gpp'
          this.consentReady = true
          this.syncConsentToShopify()
        })
      } catch (e) {
        if (this.isDebugMode()) console.warn('[SP] Eager USNAT probe failed:', e)
      }
    }

    // GDPR probe — use the canonical IAB TCF surface; `gdprApplies` is
    // only true when the GDPR scenario actually applies to this visitor.
    if (typeof window.__tcfapi === 'function') {
      try {
        window.__tcfapi('getTCData', 2, (tcData, success) => {
          if (!success || !tcData || tcData.gdprApplies !== true) return
          if (this.cmpType) return
          if (this.isDebugMode()) console.log('[SP] Eagerly routed to GDPR (already-resolved scenario)')
          this.cmpType = 'tcf'
          this.consentReady = true
          this.syncConsentToShopify()
        })
      } catch (e) {
        if (this.isDebugMode()) console.warn('[SP] Eager GDPR probe failed:', e)
      }
    }
  }

  waitForSourcepointCMP() {
    // Poll for the CMP stub to be replaced with the real implementation.
    // Previously used MutationObserver on the whole document, which fired on
    // every unrelated DOM change and didn't actually correlate to the
    // window.__tcfapi / window.__gpp globals we care about.
    const POLL_INTERVAL_MS = 250
    const TIMEOUT_MS = 15000

    const intervalId = setInterval(() => {
      if (this.isInitialized) {
        clearInterval(intervalId)
        return
      }
      if (this.bindCMP()) {
        clearInterval(intervalId)
        if (this.isDebugMode()) console.log('[SP] Sourcepoint CMP loaded, initializing integration')
        this.completeInitialization()
      }
    }, POLL_INTERVAL_MS)

    setTimeout(() => {
      clearInterval(intervalId)
      if (!this.isInitialized && this.isDebugMode()) {
        console.warn('[SP] Sourcepoint CMP not loaded within timeout period')
      }
    }, TIMEOUT_MS)
  }

  setupSourcepointEventListeners() {
    // Register BOTH listeners unconditionally when the corresponding CMP
    // stub exists. Each callback gates on `this.cmpType` so only the
    // listener whose scenario actually applies to this visitor triggers
    // a sync. This prevents the initial TCF `addEventListener` callback
    // (which fires synchronously with `gdprApplies: false` for US
    // visitors) from racing ahead of `sourcepointConsentReady` and
    // writing a wrong consent state.
    if (typeof window.__tcfapi === 'function') {
      // TCF signature: __tcfapi(command, version, callback)
      window.__tcfapi('addEventListener', 2, (tcData, success) => {
        if (!success) return
        if (this.cmpType !== 'tcf') return
        if (this.isDebugMode()) console.log('[SP] TCF consent changed:', tcData)
        this.syncConsentToShopify()
      })
    }

    if (typeof window.__gpp === 'function') {
      // GPP signature: __gpp(command, callback, parameter, apiVersion)
      // The callback receives ({ eventName, data, pingData, listenerId }, success)
      // on every GPP event. We only re-sync when signal is ready or the
      // applicable GPP sections have changed (per IAB GPP 1.1 spec).
      window.__gpp('addEventListener', (event, success) => {
        if (!success || !event) return
        if (this.cmpType !== 'gpp') return
        // Per IAB GPP 1.1, the consent-state-relevant events are
        // `signalStatus` (initial readiness) and `sectionChange`
        // (subsequent changes). `cmpStatus` is a CMP lifecycle signal
        // (loading/loaded/error) that doesn't carry consent semantics
        // — including it here just produced a redundant init-time sync
        // that `isSyncingConsent` was deduping anyway.
        const resyncEvents = ['signalStatus', 'sectionChange']
        if (!resyncEvents.includes(event.eventName)) return
        if (event.eventName === 'signalStatus' && event.data !== 'ready') return

        if (this.isDebugMode()) console.log('[SP] GPP consent changed:', event)
        this.syncConsentToShopify()
      })
    }
  }

  syncConsentToShopify() {
    if (!this.shopifyCustomerPrivacy || this.isSyncingConsent) {
      return
    }
    if (!this.cmpType) {
      // No scenario has resolved yet (waiting on `sourcepointConsentReady`).
      // Do NOT infer a default from either CMP stub — the mapping would
      // be wrong for US visitors (TCF reports `gdprApplies: false` and
      // we'd incorrectly grant everything). The first correct sync is
      // triggered when `message_type` arrives.
      return
    }

    this.isSyncingConsent = true

    // Guard: if the CMP callback never fires (adblocker, stub never resolved,
    // etc.) we must still release isSyncingConsent so subsequent events can
    // re-trigger a sync. The callbacks below all use guardedSyncCallback
    // which no-ops after the timeout has already reset state.
    const SYNC_TIMEOUT_MS = 10000
    let syncSettled = false
    const syncTimeout = setTimeout(() => {
      if (syncSettled) return
      syncSettled = true
      this.isSyncingConsent = false
      if (this.isDebugMode()) console.warn('[SP] Consent sync timed out without CMP response')
    }, SYNC_TIMEOUT_MS)

    const guardedSyncCallback = (fn) => (...args) => {
      if (syncSettled) return
      syncSettled = true
      clearTimeout(syncTimeout)
      fn(...args)
    }

    if (this.cmpType === 'tcf') {
      if (typeof window.__tcfapi !== 'function') {
        if (this.isDebugMode()) console.warn('[SP] cmpType=tcf but window.__tcfapi unavailable')
        clearTimeout(syncTimeout); syncSettled = true; this.isSyncingConsent = false
        return
      }
      window.__tcfapi('getTCData', 2, guardedSyncCallback((tcData, success) => {
        if (success && tcData) {
          const consent = this.mapTCFToShopify(tcData)

          // Update Criteo consent specifically based on TCF data
          if (typeof criteo_q !== 'undefined' && window.criteoAccount) {
            if (this.isDebugMode()) console.log('[SP] Criteo OneTag consent updated:', tcData);
            const criteoConsent = this.mapTCFToCriteo(tcData);
            this.updateCriteoConsentDirect(criteoConsent, tcData);
          }

          this.applyShopifyConsent(consent)
        } else {
          this.isSyncingConsent = false
        }
      }))
    } else if (this.cmpType === 'gpp') {
      const usnat = window._sp_ && window._sp_.usnat
      if (usnat && typeof usnat.getUserConsents === 'function') {
        usnat.getUserConsents(guardedSyncCallback((consents, success) => {
          if (!success || !consents) {
            this.isSyncingConsent = false
            return
          }
          const consent = this.mapGPPToShopify(consents)
          this.applyShopifyConsent(consent)
        }))
      } else {
        if (this.isDebugMode()) console.warn('[SP] cmpType=gpp but _sp_.usnat.getUserConsents unavailable')
        clearTimeout(syncTimeout); syncSettled = true; this.isSyncingConsent = false
      }
    }
  }

  applyShopifyConsent(consent) {
    this.shopifyCustomerPrivacy.setTrackingConsent(consent, (result) => {
      this.isSyncingConsent = false
      if (result && result.error) {
        console.error('[SP] Failed to set Shopify consent:', result.error)
      } else {
        if (this.isDebugMode()) console.log('[SP] Successfully synced consent to Shopify:', consent)
        // Allow data collection only after consent is synced
        this.allowDataCollection(consent)
      }
    })
  }


  blockDataCollection() {
    // Block all analytics scripts until consent is ready
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'default', {
        'ad_storage': 'denied',
        'analytics_storage': 'denied',
        'personalization_storage': 'denied',
        'functionality_storage': 'denied',
        'security_storage': 'granted'
      });
    }

    // Block Facebook Pixel if present
    if (typeof fbq !== 'undefined') {
      fbq('consent', 'revoke');
    }

    // Block Criteo OneTag if present
    if (typeof criteo_q !== 'undefined') {
      criteo_q.push(
        { event: "setAccount", account: window.criteoAccount || "" },
        { event: "setSiteType", type: "d" },
        { event: "setData", consent: "denied" }
      );
      if (this.isDebugMode()) console.log('[SP] Criteo OneTag consent denied until ready');
    }

    // Block Triple Whale (Triple Pixel) if present. Triple Whale does not
    // read the Sourcepoint consent string, so we gate it explicitly with the
    // same fail-closed pattern as Criteo/Facebook Pixel.
    if (typeof TriplePixel !== 'undefined') {
      TriplePixel('trackingConsent', false);
      if (this.isDebugMode()) console.log('[SP] Triple Pixel tracking consent denied until ready');
    }

    if (this.isDebugMode()) console.log('[SP] Data collection blocked until consent is ready');
  }

  allowDataCollection(consent) {
    if (!this.consentReady) {
      if (this.isDebugMode()) console.warn('[SP] Consent not ready yet, blocking data collection');
      return;
    }

    // Update Google Analytics consent
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'ad_storage': consent.marketing ? 'granted' : 'denied',
        'analytics_storage': consent.analytics ? 'granted' : 'denied',
        'personalization_storage': consent.preferences ? 'granted' : 'denied'
      });
    }

    // Update Facebook Pixel consent
    if (typeof fbq !== 'undefined') {
      if (consent.marketing) {
        fbq('consent', 'grant');
      }
    }

    // Update Criteo OneTag consent
    this.updateCriteoConsent(consent);

    // Update Triple Whale (Triple Pixel) consent
    this.updateTripleWhaleConsent(consent);

    if (this.isDebugMode()) console.log('[SP] Data collection consent updated:', consent);
  }

  mapTCFToShopify(tcData) {
    const denied = { marketing: false, analytics: false, preferences: false, sale_of_data: false }

    // We only enter this path because Sourcepoint told us the GDPR
    // scenario applies (via `message_type === 'gdpr'`). If the TCF API
    // then reports `gdprApplies: false`, that's an inconsistency between
    // Sourcepoint and its own TCF stub — fail closed rather than
    // granting a default. The earlier "grant all for non-regulated"
    // branch here was the root cause of US visitors being opted-in
    // pre-interaction via the TCF fallback.
    if (!tcData || !tcData.gdprApplies) {
      if (this.isDebugMode()) console.warn('[SP] TCF scenario selected but gdprApplies=false; denying by default');
      return denied
    }

    const consent = { marketing: false, analytics: false, preferences: false, sale_of_data: false };

    const purposeConsents = tcData.purpose && tcData.purpose.consents
    if (!purposeConsents) return consent

    // A category is granted if ANY of its mapped purposes has user consent.
    for (const [category, purposes] of Object.entries(this.tcfPurposeMapping)) {
      consent[category] = purposes.some((purposeId) => !!purposeConsents[String(purposeId)])
    }

    // GDPR/TCF doesn't regulate "sale_of_data" as a separate concept
    // (it's a CCPA/USNAT notion). For GDPR visitors, tie it to marketing
    // consent as a conservative proxy — affirmative opt-in for marketing
    // implies the user hasn't objected to their data being shared for
    // advertising purposes.
    consent.sale_of_data = consent.marketing

    return consent;
  }

  mapGPPToShopify(consents) {
    const denied = { marketing: false, analytics: false, preferences: false, sale_of_data: false }

    if (!consents) return denied

    // `applies` tells us whether the USNAT scenario applies to this
    // visitor. We require an explicit `=== true` (not `!== false`)
    // because a partial / in-flight payload could leave `applies`
    // undefined or null, and the downstream category loop would then
    // happily treat empty entries as granted via truthiness coercion.
    // Mirroring the strict check we already use for `consented`, fail
    // closed on anything that isn't an affirmative true.
    if (consents.applies !== true) {
      if (this.isDebugMode()) console.log('[SP] USNAT applies not confirmed true; denying by default')
      return denied
    }

    if (!Array.isArray(consents.categories) || consents.categories.length === 0) {
      return denied
    }

    // In Sourcepoint's USNAT scenario for this shop, the exposed
    // categories gate Sale / Sharing / Targeted Advertising (the core
    // USNAT opt-out surface). We require an explicit `consented === true`
    // — treating missing/null/undefined as denied — because pre-action
    // defaults can arrive as a non-boolean and we don't want truthiness
    // coercion to grant tracking before the user has actually chosen.
    const granted = consents.categories.every((category) => category && category.consented === true)

    // USNAT doesn't carve out "analytics" or "preferences" the way TCF
    // does. Tie them to the same category-level decision: if the user
    // hasn't opted out of Sale/Sharing/Targeted Advertising, data
    // collection is permitted; if they've opted out, everything shuts
    // off. `sale_of_data` is the direct USNAT-to-Shopify mapping.
    return {
      marketing: granted,
      analytics: granted,
      preferences: granted,
      sale_of_data: granted
    }
  }

  configurePrivacyManagerLink(messageType, info) {
    const pmLink = document.getElementById('pmLink')
    if (!pmLink) {
      if (this.isDebugMode()) console.log('[SP] #pmLink not present on page, skipping privacy manager link setup')
      return
    }

    if (!info || !info.applies) {
      return
    }

    const pmIds = (window.sourcepointConfig && window.sourcepointConfig.privacyManagerIds) || {}
    const labels = {
      usnat: 'Do Not Sell or Share My Personal Information',
      gdpr: 'Manage Preferences'
    }

    const pmId = pmIds[messageType]
    const label = labels[messageType]
    if (!pmId || !label) return

    pmLink.style.visibility = 'visible'
    pmLink.textContent = label
    pmLink.onclick = (e) => {
      e.preventDefault()
      const api = window._sp_ && window._sp_[messageType]
      if (api && typeof api.loadPrivacyManagerModal === 'function') {
        api.loadPrivacyManagerModal(pmId)
      } else {
        console.error('[SP] Privacy manager API unavailable for', messageType)
      }
    }
  }

  shouldShowBanner() {
    if (!this.shopifyCustomerPrivacy) {
      return false;
    }

    return this.shopifyCustomerPrivacy.shouldShowBanner();
  }

  getUserRegion() {
    if (!this.shopifyCustomerPrivacy) {
      return '';
    }

    return this.shopifyCustomerPrivacy.getRegion();
  }

  getConsentId() {
    if (!this.shopifyCustomerPrivacy) {
      return null;
    }

    return this.shopifyCustomerPrivacy.consentId();
  }

  updateTripleWhaleConsent(consent) {
    // Triple Whale does not read the Sourcepoint consent string, so the theme
    // gates its Triple Pixel via TriplePixel('trackingConsent', ...). Gate on
    // marketing consent — same category as Facebook Pixel — since Triple Whale
    // is an ad-attribution pixel. Called on every sync, so opt-out / Do Not
    // Sell re-syncs (marketing: false) revoke consent without a separate
    // listener. Triple Pixel is loaded by the Triple Whale Shopify app embed
    // block, not the theme, so guard on its presence.
    if (typeof TriplePixel === 'undefined') {
      if (this.isDebugMode()) console.log('[SP] Triple Pixel not available');
      return;
    }

    const grant = consent.marketing === true;

    try {
      TriplePixel('trackingConsent', grant);
      if (this.isDebugMode()) console.log('[SP] Triple Pixel tracking consent updated:', grant);
    } catch (error) {
      console.error('[SP] Failed to update Triple Pixel consent:', error);
    }
  }

  updateCriteoConsent(consent) {
    if (typeof criteo_q === 'undefined') {
      if (this.isDebugMode()) console.log('[SP] Criteo OneTag not available');
      return;
    }

    // Map Shopify consent to Criteo OneTag format
    const criteoConsent = consent.marketing ? "granted" : "denied";

    try {
      criteo_q.push(
        { event: "setAccount", account: window.criteoAccount || "" },
        { event: "setSiteType", type: "d" },
        { event: "setData", consent: criteoConsent }
      );

      // If marketing consent is granted, enable Criteo tracking
      if (consent.marketing) {
        criteo_q.push({ event: "viewItem" });
      }

      if (this.isDebugMode()) console.log('[SP] Criteo OneTag consent updated:', criteoConsent);
    } catch (error) {
      console.error('[SP] Failed to update Criteo consent:', error);
    }
  }

  mapTCFToCriteo(tcData) {
    if (!tcData || !tcData.purpose || !tcData.purpose.consents) {
      return false;
    }

    const purposeConsents = tcData.purpose.consents;

    // Criteo requires Purpose 3 (personalized ads) and Purpose 4 (ad selection)
    // Also check for legitimate interest if consents are not available
    const hasMarketingConsent = purposeConsents['3'] || purposeConsents['4'];
    const hasLegitimateInterest = tcData.purpose.legitimateInterests &&
      (tcData.purpose.legitimateInterests['3'] || tcData.purpose.legitimateInterests['4']);

    // Check vendor consent for Criteo (vendor ID 91)
    const hasCriteoVendorConsent = tcData.vendor && tcData.vendor.consents && tcData.vendor.consents['91'];
    const hasCriteoLegitimateInterest = tcData.vendor && tcData.vendor.legitimateInterests && tcData.vendor.legitimateInterests['91'];

    return (hasMarketingConsent || hasLegitimateInterest) && (hasCriteoVendorConsent || hasCriteoLegitimateInterest);
  }

  updateCriteoConsentDirect(hasConsent, tcData) {
    if (typeof criteo_q === 'undefined') {
      if (this.isDebugMode()) console.log('[SP] Criteo OneTag not available for direct TCF update');
      return;
    }

    try {
      const criteoConsent = hasConsent ? "granted" : "denied";

      criteo_q.push(
        { event: "setAccount", account: window.criteoAccount },
        { event: "setSiteType", type: "d" },
        { event: "setData", consent: criteoConsent }
      );

      // If consent is granted and we have valid TCF data, track view
      if (hasConsent && tcData && tcData.gdprApplies) {
        criteo_q.push({ event: "viewItem" });
      }

      if (this.isDebugMode()) console.log('[SP] Criteo OneTag TCF consent updated:', criteoConsent, 'TCF Applied:', tcData?.gdprApplies);
    } catch (error) {
      console.error('[SP] Failed to update Criteo TCF consent:', error);
    }
  }

}

document.addEventListener('DOMContentLoaded', function() {
  if (window.sourcepointConfig && window.sourcepointConfig.enabled) {
    window.sourcepointCMPIntegration = new theme.SourcepointCMP();
  }
});

window.SourcepointCMP = theme.SourcepointCMP;
